import { cn } from "@/lib/utils.ts";
import type { CSSProperties, ReactNode } from "react";

/**
 * One full-height column of a split layout: its own header row on top and a body
 * that fills the rest. The draggable divider runs the panel's full height, so
 * the header sits flush against it.
 *
 * The root is the column's positioning context, so an overlay that must cover
 * the whole column — the canvas card's scrim — spans the header too rather than
 * stopping at the body. `CanvasHost` deliberately leaves itself unpositioned to
 * defer to this; see the note there before adding `relative` to either.
 */
export const SplitPanelFrame = ({
  header,
  footer,
  children,
  className,
  style,
}: {
  header: ReactNode;
  /**
   * Pinned to the bottom of the column, below the scrolling body. For chrome
   * that belongs to the column rather than to what it is listing — the sidebar's
   * account switcher and settings gear.
   */
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
  /**
   * For custom properties the column's own contents compute at runtime — a
   * className can only carry values known at build time. Sits alongside
   * `--panel-gutter` below, which is static and so stays a class.
   */
  style?: CSSProperties;
}) => (
  // `flex-1 min-w-0` so the panel fills width both inside the split's row and when
  // rendered on its own (a single full-width panel in the collapsed states).
  // `--panel-gutter` is the column's single side-inset, read by the header and
  // by every body that has to line up with it. One declaration is the point:
  // a body carrying its own copy resolves against the panel's `@container`
  // width, which the narrow left column never satisfies, so it silently sits
  // 10px inside the header at desktop widths.
  //
  // Viewport-based rather than container-based so both columns' headers inset
  // by the same amount at a given window width — a container query would give
  // the narrow sidebar a different inset from the canvas beside it, and the two
  // headers' contents would stop lining up as the divider is dragged.
  <div
    className={cn(
      "relative flex h-full min-h-0 min-w-0 flex-1 flex-col",
      // One value at every width; see the note on `--panel-gutter` in index.css
      // for why the lg step-up to 1.625rem went.
      "[--panel-gutter:1rem]",
      className,
    )}
    style={style}
  >
    {header}
    <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    {/* Outside the body's flex child, so it keeps its height while the body
        scrolls under it rather than being pushed off the bottom by a long list. */}
    {footer}
  </div>
);
